import { useState, useEffect, useCallback } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { converters } from "@/lib/firestore";
import { useAuth } from "@/hooks/useAuth";
import type { Org } from "@/types/firestore";

export interface OrgWithId extends Org {
  id: string;
}

export function useOrgs() {
  const { user } = useAuth();
  const [orgs, setOrgs] = useState<OrgWithId[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ref = collection(db, "orgs").withConverter(converters.orgs);
    const q = query(ref, orderBy("createdAt", "desc"), limit(200));

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        setOrgs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching orgs:", err);
        setError("Failed to load organizations.");
        setLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const createOrg = useCallback(
    async (data: {
      name: string;
      slug: string;
      ownerName?: string;
      ownerEmail?: string;
    }) => {
      if (!user) throw new Error("Not authenticated");
      const ref = doc(collection(db, "orgs"));
      await setDoc(ref, {
        name: data.name,
        slug: data.slug,
        logo: null,
        status: "active",
        ownerId: null,
        createdAt: serverTimestamp(),
        createdBy: user.uid,
        updatedAt: serverTimestamp(),
      });

      // If owner info provided, invite them as ORG_OWNER
      // (inviteOrgUser will set ownerId on the org doc)
      if (data.ownerEmail && data.ownerName) {
        try {
          const inviteOrgUserFn = httpsCallable<
            { email: string; name: string; role: string; orgId: string },
            { success: boolean; error?: string }
          >(functions, "inviteOrgUser");
          const result = await inviteOrgUserFn({
            email: data.ownerEmail,
            name: data.ownerName,
            role: "ORG_OWNER",
            orgId: ref.id,
          });
          if (!result.data.success) {
            throw new Error(result.data.error ?? "Failed to invite owner.");
          }
        } catch (err) {
          const message = err instanceof Error ? err.message : "Failed to invite owner.";
          throw new Error(`Organization created, but owner invitation failed: ${message}`);
        }
      }

      return ref.id;
    },
    [user]
  );

  const updateOrg = useCallback(
    async (orgId: string, data: Partial<Omit<Org, "createdAt" | "createdBy">>) => {
      const ref = doc(db, "orgs", orgId);
      await updateDoc(ref, {
        ...data,
        updatedAt: serverTimestamp(),
      });
    },
    []
  );

  const deleteOrg = useCallback(async (orgId: string) => {
    const ref = doc(db, "orgs", orgId);
    await deleteDoc(ref);
  }, []);

  return { orgs, loading, error, createOrg, updateOrg, deleteOrg };
}
